import type { GoalArenaShotOutcome } from '../../../shared/results';
import { goalAudioPresentation as P } from './three/goalArenaPresentation';

const MUTE_STORAGE_KEY = 'odevlendir-goal-muted';

export class FootballAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private reverb: ConvolverNode | null = null;
  private ambience: AudioBufferSourceNode | null = null;
  private muted = this.readMuted();

  private readMuted() {
    try { return window.localStorage.getItem(MUTE_STORAGE_KEY) === 'true'; } catch { return false; }
  }

  private noiseBuffer(context: AudioContext, duration: number) {
    const length = Math.max(1, Math.floor(context.sampleRate * duration));
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    let previous = 0;
    for (let index = 0; index < length; index += 1) {
      const white = Math.random() * 2 - 1;
      previous = previous * 0.2 + white * 0.8;
      data[index] = previous;
    }
    return buffer;
  }

  private impulse(context: AudioContext) {
    const length = Math.floor(context.sampleRate * P.reverbSeconds);
    const buffer = context.createBuffer(2, length, context.sampleRate);
    for (let channel = 0; channel < 2; channel += 1) {
      const data = buffer.getChannelData(channel);
      for (let index = 0; index < length; index += 1) data[index] = (Math.random() * 2 - 1) * (1 - index / length) ** P.reverbDecay;
    }
    return buffer;
  }

  private ensureContext() {
    if (this.context && this.master) {
      if (this.context.state === 'suspended') void this.context.resume();
      return this.context;
    }
    const context = new AudioContext();
    const master = context.createGain();
    const compressor = context.createDynamicsCompressor();
    compressor.threshold.value = -18; compressor.knee.value = 16; compressor.ratio.value = 4; compressor.attack.value = 0.004; compressor.release.value = 0.22;
    master.gain.value = this.muted ? 0 : P.masterGain;
    master.connect(compressor).connect(context.destination);
    const reverb = context.createConvolver(); reverb.buffer = this.impulse(context); reverb.connect(master);
    this.context = context; this.master = master; this.reverb = reverb;
    this.startAmbience();
    return context;
  }

  private connect(node: AudioNode, wet = 0.12) {
    if (!this.master) return;
    node.connect(this.master);
    if (this.reverb && wet > 0 && this.context) {
      const send = this.context.createGain(); send.gain.value = wet; node.connect(send).connect(this.reverb);
    }
  }

  private tone(frequency: number, duration: number, gain: number, type: OscillatorType = 'sine', end = frequency, delay = 0, wet = 0.12) {
    const context = this.ensureContext(); const starts = context.currentTime + delay;
    const oscillator = context.createOscillator(); const volume = context.createGain(); const filter = context.createBiquadFilter();
    oscillator.type = type; oscillator.frequency.setValueAtTime(frequency, starts); oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, end), starts + duration);
    filter.type = 'lowpass'; filter.frequency.value = Math.max(800, Math.min(12000, frequency * 8));
    volume.gain.setValueAtTime(0.0001, starts); volume.gain.exponentialRampToValueAtTime(gain, starts + 0.008); volume.gain.exponentialRampToValueAtTime(0.0001, starts + duration);
    oscillator.connect(filter).connect(volume); this.connect(volume, wet); oscillator.start(starts); oscillator.stop(starts + duration + 0.03);
  }

  private noise(duration: number, gain: number, startHz: number, endHz: number, delay = 0, wet = 0.12) {
    const context = this.ensureContext(); const starts = context.currentTime + delay;
    const source = context.createBufferSource(); const filter = context.createBiquadFilter(); const volume = context.createGain();
    source.buffer = this.noiseBuffer(context, duration + 0.02); filter.type = 'bandpass'; filter.Q.value = 0.8;
    filter.frequency.setValueAtTime(startHz, starts); filter.frequency.exponentialRampToValueAtTime(Math.max(40, endHz), starts + duration);
    volume.gain.setValueAtTime(0.0001, starts); volume.gain.exponentialRampToValueAtTime(gain, starts + 0.012); volume.gain.exponentialRampToValueAtTime(0.0001, starts + duration);
    source.connect(filter).connect(volume); this.connect(volume, wet); source.start(starts); source.stop(starts + duration + 0.04);
  }

  private startAmbience() {
    if (!this.context || !this.master || this.ambience) return;
    const source = this.context.createBufferSource(); const filter = this.context.createBiquadFilter(); const gain = this.context.createGain();
    source.buffer = this.noiseBuffer(this.context, 2.8); source.loop = true; filter.type = 'bandpass'; filter.frequency.value = 410; filter.Q.value = 0.5; gain.gain.value = P.ambienceGain;
    source.connect(filter).connect(gain).connect(this.master); source.start(); this.ambience = source;
  }

  isMuted() { return this.muted; }
  unlock() { this.ensureContext(); }
  toggleMuted() {
    this.muted = !this.muted; const context = this.ensureContext(); this.master?.gain.setTargetAtTime(this.muted ? 0 : P.masterGain, context.currentTime, 0.025);
    try { window.localStorage.setItem(MUTE_STORAGE_KEY, String(this.muted)); } catch { /* optional */ }
    return this.muted;
  }

  kick() {
    this.unlock();
    this.tone(86, 0.11, 0.17, 'sine', 52, 0, 0.08);
    this.noise(0.12, 0.11, 1900, 620, 0, 0.16);
    this.noise(0.34, 0.035, 1600, 4800, 0.05, 0.12);
  }

  impact(outcome: GoalArenaShotOutcome, streak: number) {
    if (outcome === 'goal') {
      this.noise(0.48, 0.14, 6600, 1900, 0, 0.3);
      this.noise(1.05, Math.min(0.14, 0.075 + streak * 0.009), 430, 760, 0.04, 0.45);
      [523, 659, 784].forEach((frequency, index) => this.tone(frequency, 0.26, 0.045, 'triangle', frequency * 1.015, 0.07 + index * 0.07, 0.3));
      return;
    }
    if (outcome === 'post' || outcome === 'crossbar') {
      [520, 910, 1480].forEach((frequency, index) => this.tone(frequency, 0.22 - index * 0.03, 0.09 / (index + 1), 'triangle', frequency * 0.82, index * 0.006, 0.42));
    } else if (outcome === 'save') {
      this.tone(118, 0.14, 0.14, 'sine', 72, 0, 0.22); this.noise(0.17, 0.1, 1400, 520, 0, 0.22);
    } else {
      this.noise(0.25, 0.045, 780, 340, 0, 0.2);
    }
    this.tone(260, 0.22, 0.035, 'triangle', 195, 0.1, 0.25);
    this.tone(195, 0.28, 0.03, 'sine', 146, 0.19, 0.28);
  }

  finish(perfect = false) {
    const notes = perfect ? [523, 659, 784, 1047] : [392, 523, 659, 784];
    notes.forEach((frequency, index) => this.tone(frequency, 0.42, 0.055, 'triangle', frequency * 1.01, index * 0.1, 0.38));
    this.noise(1.4, perfect ? 0.13 : 0.09, 420, 820, 0.08, 0.5);
  }

  dispose() {
    try { this.ambience?.stop(); } catch { /* already stopped */ }
    this.ambience = null; void this.context?.close(); this.context = null; this.master = null; this.reverb = null;
  }
}
